import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, X } from "lucide-react";
import { Button } from "../components";
import { ShareStreakCard } from "../components/ShareStreakCard";
import { voiceVolume, getStoredAudioMode } from "../utils/modeAudio";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Breathing audio files
const THREE_BREATHS_AUDIO_URL = `${API_URL}/api/breathing-audio/3_breaths.m4a`;
const TWO_BREATHS_AUDIO_URL = `${API_URL}/api/breathing-audio/2_breaths.m4a`;

// Mood options for Step 1
const MOOD_OPTIONS = [
  { id: "calm", label: "Calm" },
  { id: "stressed", label: "Stressed" },
  { id: "low_energy", label: "Low Energy" },
  { id: "focused", label: "Focused" },
  { id: "grateful", label: "Grateful" },
];

// Intention options for Step 2
const INTENTION_OPTIONS = [
  { id: "presence", label: "Presence" },
  { id: "clarity", label: "Clarity" },
  { id: "confidence", label: "Confidence" },
  { id: "flow", label: "Flow" },
  { id: "peace", label: "Peace" },
];

export const DailyRitualPage = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(0); // Start at 0 for entry screen
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedIntention, setSelectedIntention] = useState(null);
  const [breathPhase, setBreathPhase] = useState("ready"); // ready, inhale, hold, release, rest, stillness, complete
  const [breathCount, setBreathCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [showShareCard, setShowShareCard] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  
  // Gratitude moment state
  const [gratitudeAudioUrl, setGratitudeAudioUrl] = useState(null);
  const [gratitudeAudioPlaying, setGratitudeAudioPlaying] = useState(false);
  const gratitudeAudioRef = useRef(null);
  
  const audioRef = useRef(null);

  const getToken = () => localStorage.getItem("reclaim_token");

  // Play the 3-breath voice guidance
  const playRitualAudio = () => {
    try {
      const audio = new Audio(THREE_BREATHS_AUDIO_URL);
      audio.volume = voiceVolume(getStoredAudioMode());
      audioRef.current = audio;
      audio.play().catch(err => console.log("Audio play failed:", err));
    } catch (err) {
      console.log("Ritual audio error:", err);
    }
  };

  // Stop audio when ritual ends
  const stopRitualAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  // Generate gratitude voice cue using Michael's cloned voice
  const generateGratitudeAudio = async () => {
    try {
      const token = getToken();
      if (!token) return;
      
      const response = await fetch(`${API_URL}/api/daily-ritual/gratitude-audio`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.audio_url) {
          setGratitudeAudioUrl(data.audio_url);
        }
      }
    } catch (err) {
      console.log("Could not generate gratitude audio:", err);
    }
  };

  // Play gratitude audio
  const playGratitudeAudio = () => {
    if (!gratitudeAudioUrl) return;
    
    try {
      const audio = new Audio(gratitudeAudioUrl);
      audio.volume = voiceVolume(getStoredAudioMode());
      gratitudeAudioRef.current = audio;
      
      audio.onplay = () => setGratitudeAudioPlaying(true);
      audio.onended = () => setGratitudeAudioPlaying(false);
      audio.onerror = () => setGratitudeAudioPlaying(false);
      
      audio.play().catch(err => console.log("Gratitude audio play failed:", err));
    } catch (err) {
      console.log("Gratitude audio error:", err);
    }
  };

  // Stop gratitude audio
  const stopGratitudeAudio = () => {
    if (gratitudeAudioRef.current) {
      gratitudeAudioRef.current.pause();
      gratitudeAudioRef.current = null;
    }
  };

  // Pre-generate gratitude audio when component mounts
  useEffect(() => {
    generateGratitudeAudio();
    return () => {
      stopGratitudeAudio();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Meditation chime sound using Web Audio API
  const playMeditationChime = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create a gentle chime sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Soft, warm frequency (A4 = 440Hz, but we'll use a softer 528Hz - "healing frequency")
      oscillator.frequency.setValueAtTime(528, audioContext.currentTime);
      oscillator.type = 'sine';
      
      // Gentle fade in and out
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 2);
    } catch (err) {
      console.log("Audio not supported:", err);
    }
  };

  // Reclaim Power Breath - 7-7-7-7 pattern
  // Inhale (7s) → Hold (7s) → Release (7s) → Rest (7s) = 28s per breath
  // 3 breaths total = ~84 seconds
  useEffect(() => {
    if (step !== 3 || breathPhase === "ready" || breathPhase === "complete" || breathPhase === "stillness") return;

    // Phase duration synced with 3-breath audio (82.77s / 12 phases = 6897ms)
    const PHASE_DURATION = 6897;
    
    const phases = {
      inhale: { next: "hold", duration: PHASE_DURATION },
      hold: { next: "release", duration: PHASE_DURATION },
      release: { next: "rest", duration: PHASE_DURATION },
      rest: { next: breathCount < 2 ? "inhale" : "stillness", duration: PHASE_DURATION },
    };

    const timer = setTimeout(() => {
      const current = phases[breathPhase];
      if (current.next === "inhale") {
        setBreathCount(prev => prev + 1);
      }
      setBreathPhase(current.next);
    }, phases[breathPhase].duration);

    return () => clearTimeout(timer);
  }, [breathPhase, breathCount, step]);

  // Stillness moment after 3 breaths - pause before completion
  useEffect(() => {
    if (breathPhase !== "stillness") return;
    
    // Wait for "Notice how you feel" in the audio
    const timer = setTimeout(() => {
      setBreathPhase("complete");
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [breathPhase]);

  // Cleanup audio ONLY on component unmount
  useEffect(() => {
    return () => stopRitualAudio();
  }, []);

  const startBreathing = () => {
    // Play the full ritual audio - voice guides through everything
    playRitualAudio();
    
    // Start visual animation immediately - audio is trimmed to match visuals
    // The audio starts with the breathing guidance right away
    setTimeout(() => {
      playMeditationChime();
      setBreathPhase("inhale");
      setBreathCount(0);
    }, 500);  // Small delay just for the audio to start loading
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/daily-ritual/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mood: selectedMood,
          intention: selectedIntention,
        }),
      });
      const data = await res.json();
      setCompletionData(data);
      setStep(4); // Move to gratitude moment (0=entry, 1=mood, 2=intention, 3=breathing, 4=gratitude, 5=complete)
    } catch (err) {
      console.error("Error saving ritual:", err);
      // Still show gratitude even if save fails
      setCompletionData({ streak: 1, streak_theme: INTENTION_OPTIONS.find(i => i.id === selectedIntention)?.label || "Presence" });
      setStep(4);
    }
    setSaving(false);
  };

  const handleFinish = () => {
    localStorage.setItem("reclaim_ritual_completed", new Date().toDateString());
    // Soft fade out before completing
    setIsExiting(true);
    setTimeout(() => {
      onComplete?.();
    }, 500);
  };

  const handleSkip = () => {
    // Mark as skipped for today (stored in localStorage)
    localStorage.setItem("reclaim_ritual_skipped", new Date().toDateString());
    setIsExiting(true);
    setTimeout(() => {
      onSkip?.();
    }, 500);
  };

  // Premium transition settings
  const stepTransition = {
    duration: 0.5,
    ease: [0.4, 0, 0.2, 1] // Custom easing for smooth, premium feel
  };

  const buttonPressAnimation = {
    scale: 0.95,
    transition: { duration: 0.15 }
  };

  const buttonHoverAnimation = {
    scale: 1.02,
    y: -2,
    transition: { duration: 0.2 }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: isExiting ? 0 : 1 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
      className="fixed inset-0 z-[100] flex flex-col"
      style={{ background: 'linear-gradient(180deg, #1f3a5c 0%, #0c1a30 55%, #03060f 100%)' }}
    >
      {/* Close X button - always visible on all screens except completion */}
      {step < 5 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="absolute top-6 right-6 z-10"
        >
          <motion.button
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleSkip(); }}
            whileHover={{ scale: 1.1, backgroundColor: "rgba(255,255,255,0.2)" }}
            whileTap={{ scale: 0.9 }}
            className="p-2 rounded-full bg-white/10 transition-colors"
            data-testid="close-ritual-btn"
            aria-label="Close daily ritual"
          >
            <X size={20} className="text-white/70" />
          </motion.button>
        </motion.div>
      )}

      {/* Progress indicator - only show for steps 1-3 */}
      {step >= 1 && step <= 3 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="flex justify-center gap-3 pt-8 pb-4"
        >
          {[1, 2, 3].map((s) => (
            <motion.div
              key={s}
              animate={{ 
                opacity: s <= step ? 1 : 0.3,
                scale: s === step ? 1 : 0.8
              }}
              transition={{ duration: 0.3 }}
              className="w-2 h-2 rounded-full bg-white"
            />
          ))}
        </motion.div>
      )}

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8">
        <AnimatePresence mode="wait">
          {/* Step 0: Entry Screen */}
          {step === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="w-full max-w-sm text-center"
            >
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="font-heading text-4xl text-white mb-6"
              >
                Welcome back.
                <br />
                Let's begin.
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="text-white/60 text-lg mb-12 leading-relaxed"
              >
                A 60-second ritual to reset your mind
                <br />
                and start the day with clarity.
              </motion.p>

              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="text-white/40 text-sm mb-12 italic"
              >
                Take a slow breath… then begin.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="space-y-4"
              >
                <Button
                  onClick={() => setStep(1)}
                  className="px-12 py-4"
                  data-testid="start-ritual-btn"
                >
                  Start Ritual
                </Button>
                
                <button
                  onClick={onSkip}
                  className="block mx-auto text-white/40 hover:text-white/60 text-sm underline transition-colors"
                  data-testid="skip-ritual-btn"
                >
                  Skip for now
                </button>
              </motion.div>
            </motion.div>
          )}

          {/* Step 1: Mood Check */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={stepTransition}
              className="w-full max-w-sm text-center"
            >
              <motion.h2 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                className="font-heading text-3xl text-white mb-12"
              >
                How are you feeling right now?
              </motion.h2>

              <div className="space-y-3">
                {MOOD_OPTIONS.map((mood, index) => (
                  <motion.button
                    key={mood.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 + index * 0.08, duration: 0.3 }}
                    whileHover={buttonHoverAnimation}
                    whileTap={buttonPressAnimation}
                    onClick={() => {
                      setSelectedMood(mood.id);
                      setTimeout(() => setStep(2), 300);
                    }}
                    className={`w-full py-4 px-6 rounded-2xl text-lg transition-colors ${
                      selectedMood === mood.id
                        ? "bg-white text-[#1F2937]"
                        : "bg-white/10 text-white hover:bg-white/15"
                    }`}
                    data-testid={`mood-${mood.id}`}
                  >
                    {mood.label}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Intention */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={stepTransition}
              className="w-full max-w-sm text-center"
            >
              <motion.h2 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                className="font-heading text-3xl text-white mb-12"
              >
                What would you like to reclaim today?
              </motion.h2>

              <div className="space-y-3">
                {INTENTION_OPTIONS.map((intention, index) => (
                  <motion.button
                    key={intention.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 + index * 0.08, duration: 0.3 }}
                    whileHover={buttonHoverAnimation}
                    whileTap={buttonPressAnimation}
                    onClick={() => {
                      setSelectedIntention(intention.id);
                      setTimeout(() => setStep(3), 300);
                    }}
                    className={`w-full py-4 px-6 rounded-2xl text-lg transition-colors ${
                      selectedIntention === intention.id
                        ? "bg-white text-[#1F2937]"
                        : "bg-white/10 text-white hover:bg-white/15"
                    }`}
                    data-testid={`intention-${intention.id}`}
                  >
                    {intention.label}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Reclaim Power Breath */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={stepTransition}
              className="w-full max-w-sm text-center"
            >
              {breathPhase === "ready" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="text-center"
                >
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    className="w-32 h-32 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-10"
                  >
                    <div className="w-20 h-20 rounded-full bg-white/15" />
                  </motion.div>
                  
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.4 }}
                    className="text-white/70 text-lg mb-4"
                  >
                    Take a slow breath…
                  </motion.p>
                  
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.4 }}
                    className="text-white/50 mb-12"
                  >
                    We will take three power breaths together.
                    <br />
                    Follow the rhythm of the breath.
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.4 }}
                  >
                    <Button
                      onClick={startBreathing}
                      className="px-10 py-4"
                      data-testid="start-breathing-btn"
                    >
                      Begin
                    </Button>
                  </motion.div>
                </motion.div>
              )}

              {/* Active breathing phases */}
              {(breathPhase === "inhale" || breathPhase === "hold" || breathPhase === "release" || breathPhase === "rest") && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6 }}
                  className="flex flex-col items-center"
                >
                  {/* Breath counter */}
                  <motion.p
                    key={breathCount}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-white/40 text-sm mb-8"
                  >
                    Breath {breathCount + 1} of 3
                  </motion.p>

                  {/* Breathing circle - expands on inhale, holds, contracts on release, neutral on rest */}
                  <motion.div
                    animate={{
                      scale: breathPhase === "inhale" ? 1.5 : 
                             breathPhase === "hold" ? 1.5 : 
                             breathPhase === "release" ? 1 : 
                             1,
                      boxShadow: breathPhase === "inhale" 
                        ? "0 0 60px 25px rgba(100, 160, 220, 0.25)"
                        : breathPhase === "hold"
                        ? "0 0 80px 35px rgba(100, 160, 220, 0.35)"
                        : breathPhase === "release"
                        ? "0 0 30px 10px rgba(100, 160, 220, 0.15)"
                        : "0 0 20px 5px rgba(100, 160, 220, 0.1)",
                    }}
                    transition={{ duration: 7, ease: "easeInOut" }}
                    className="w-40 h-40 rounded-full bg-[#E3F0FD]/10 flex items-center justify-center mb-12"
                  >
                    <motion.div
                      animate={{
                        scale: breathPhase === "inhale" ? 1.4 : 
                               breathPhase === "hold" ? 1.4 : 
                               breathPhase === "release" ? 1 : 
                               0.9,
                        backgroundColor: breathPhase === "hold" 
                          ? "rgba(100, 160, 220, 0.3)" 
                          : breathPhase === "rest"
                          ? "rgba(227, 240, 253, 0.1)"
                          : "rgba(227, 240, 253, 0.2)",
                      }}
                      transition={{ duration: 7, ease: "easeInOut" }}
                      className="w-24 h-24 rounded-full"
                    />
                  </motion.div>

                  {/* Phase instruction */}
                  <motion.p
                    key={breathPhase}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="text-white/60 text-xl font-heading"
                  >
                    {breathPhase === "inhale" && "Breathe in…"}
                    {breathPhase === "hold" && "Hold…"}
                    {breathPhase === "release" && "And release…"}
                    {breathPhase === "rest" && "Breathe normally…"}
                  </motion.p>
                </motion.div>
              )}

              {/* Stillness moment after 3 breaths */}
              {breathPhase === "stillness" && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1 }}
                  className="flex flex-col items-center"
                >
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 3, ease: "easeInOut" }}
                    className="w-32 h-32 rounded-full bg-[#E3F0FD]/10 flex items-center justify-center mb-12"
                    style={{ boxShadow: "0 0 40px 15px rgba(100, 160, 220, 0.2)" }}
                  >
                    <div className="w-20 h-20 rounded-full bg-[#E3F0FD]/15" />
                  </motion.div>

                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 1 }}
                    className="text-white/70 text-xl font-heading italic"
                  >
                    Notice how you feel.
                  </motion.p>
                </motion.div>
              )}

              {/* Completion - ready to continue */}
              {breathPhase === "complete" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6 }}
                  className="text-center"
                >
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    className="w-20 h-20 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-10"
                  >
                    <svg className="w-10 h-10 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                  >
                    <Button
                      onClick={handleComplete}
                      disabled={saving}
                      className="px-10 py-4"
                      data-testid="complete-ritual-btn"
                    >
                      {saving ? "Saving…" : "Continue"}
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Step 4: Gratitude Moment */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={stepTransition}
              className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6"
              onAnimationComplete={() => {
                // Auto-play gratitude audio when step appears
                playGratitudeAudio();
              }}
            >
              {/* Gentle pulsing visual */}
              <motion.div
                className="relative mb-12"
              >
                {/* Outer glow rings */}
                <motion.div
                  animate={{ 
                    scale: [1, 1.3, 1],
                    opacity: [0.2, 0.4, 0.2]
                  }}
                  transition={{ 
                    duration: 4, 
                    ease: "easeInOut",
                    repeat: Infinity 
                  }}
                  className="absolute inset-0 w-40 h-40 -m-4 rounded-full bg-gradient-to-br from-sky-400/30 to-blue-500/30 blur-xl"
                />
                <motion.div
                  animate={{ 
                    scale: [1.1, 1.4, 1.1],
                    opacity: [0.15, 0.3, 0.15]
                  }}
                  transition={{ 
                    duration: 4, 
                    ease: "easeInOut",
                    repeat: Infinity,
                    delay: 0.5
                  }}
                  className="absolute inset-0 w-48 h-48 -m-8 rounded-full bg-gradient-to-br from-cyan-400/20 to-blue-500/20 blur-2xl"
                />
                
                {/* Center orb */}
                <motion.div
                  animate={{ 
                    scale: [1, 1.08, 1],
                  }}
                  transition={{ 
                    duration: 3, 
                    ease: "easeInOut",
                    repeat: Infinity 
                  }}
                  className="relative w-32 h-32 rounded-full bg-gradient-to-br from-sky-400/40 to-blue-500/40 flex items-center justify-center backdrop-blur-sm"
                >
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                    }}
                    transition={{ 
                      duration: 2.5, 
                      ease: "easeInOut",
                      repeat: Infinity,
                      delay: 0.3
                    }}
                    className="w-20 h-20 rounded-full bg-gradient-to-br from-sky-300/50 to-blue-400/50"
                  />
                </motion.div>
              </motion.div>

              {/* Gratitude text */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="max-w-md"
              >
                <motion.p
                  className="text-white/90 text-xl leading-relaxed font-light mb-4"
                >
                  Right now, this is your opportunity to fall deep into gratitude.
                </motion.p>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.5, duration: 0.8 }}
                  className="text-white/80 text-lg leading-relaxed font-light mb-4"
                >
                  Go ahead—tune into what makes you feel truly grateful.
                </motion.p>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2.5, duration: 0.8 }}
                  className="text-sky-300/90 text-lg leading-relaxed font-light italic"
                >
                  Not just in thought—feel it on every level.
                </motion.p>
              </motion.div>

              {/* Audio indicator */}
              {gratitudeAudioPlaying && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-8 flex items-center gap-2 text-sky-400/60"
                >
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="w-2 h-2 rounded-full bg-sky-400/60"
                  />
                  <span className="text-xs">Your voice</span>
                </motion.div>
              )}

              {/* Continue button - appears after a delay */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 6, duration: 0.6 }}
                className="mt-12"
              >
                <Button
                  onClick={() => {
                    stopGratitudeAudio();
                    setStep(5);
                  }}
                  className="px-10 py-4 bg-gradient-to-r from-sky-500/80 to-blue-500/80 hover:from-sky-500 hover:to-blue-500"
                  data-testid="gratitude-continue-btn"
                >
                  Continue
                </Button>
              </motion.div>
            </motion.div>
          )}

          {/* Step 5: Completion Screen */}
          {step === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={stepTransition}
              className="w-full max-w-sm text-center"
            >
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="font-heading text-3xl text-white mb-12"
              >
                You showed up for yourself today.
              </motion.h2>

              {/* Streak info */}
              {completionData && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                  className="mb-12"
                >
                  <p className="text-white/60 text-lg">
                    Day {completionData.streak} of your {completionData.streak_theme} streak.
                  </p>
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="space-y-4"
              >
                {/* Share button for milestones */}
                {completionData?.milestone_reached && (
                  <Button
                    variant="outline"
                    onClick={() => setShowShareCard(true)}
                    className="w-full py-4 border-white/30 text-white hover:bg-white/10"
                    data-testid="share-streak-btn"
                  >
                    <Share2 size={18} />
                    Share
                  </Button>
                )}
                <Button
                  onClick={handleFinish}
                  className="px-12 py-4"
                  data-testid="finish-ritual-btn"
                >
                  Continue
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Share Streak Card Modal */}
      {completionData && (
        <ShareStreakCard
          streak={completionData.streak}
          streakTheme={completionData.streak_theme}
          intention={selectedIntention}
          onClose={() => setShowShareCard(false)}
          isVisible={showShareCard}
        />
      )}
    </motion.div>
  );
};

export default DailyRitualPage;
